import { useEffect, useMemo, useRef, useState } from "react";
import { WE_RIDER_LOGO_URL as weRiderLogo } from "../../services/local/institutionalBranding";
import {
  RIDER_LIVENESS_METHOD,
  advanceLivenessChallenge,
  createChallengeState,
  createFaceLivenessDetector,
  createLivenessChallengeSequence,
  getLivenessChallengeCopy,
} from "../../services/local/weRiderLiveness";
import { getRequiredWeRiderDocumentTypes } from "../../services/supabase/weRiderService";

const DOCUMENT_COPY = Object.freeze({
  dni_front: { label: "DNI de frente", capture: "environment" },
  dni_back: { label: "DNI de dorso", capture: "environment" },
  selfie: { label: "Selfie en vivo" },
  driver_license_front: {
    label: "Licencia de conducir de frente",
    capture: "environment",
  },
  driver_license_back: {
    label: "Licencia de conducir de dorso",
    capture: "environment",
  },
  vehicle_registration_front: {
    label: "Cédula del vehículo de frente",
    capture: "environment",
  },
  vehicle_registration_back: {
    label: "Cédula del vehículo de dorso",
    capture: "environment",
  },
});

const MAX_FILE_SIZE = 8 * 1024 * 1024;
const ALLOWED_FILE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const LIVE_SELFIE_MAX_AGE_MS = 15 * 60 * 1000;
const LIVENESS_CHALLENGE_TIMEOUT_MS = 20 * 1000;
const MINIMUM_RIDER_AGE = 18;
const RIDER_IDENTITY_CONSENT_VERSION = "2026-09-03";
const RIDER_TERMS_VERSION = "2026-09-03";
const RIDER_PRIVACY_VERSION = "2026-09-03";
const VEHICLE_OPTIONS = [
  { value: "motorcycle", label: "Moto", requiresDocuments: true },
  { value: "bicycle", label: "Bicicleta", requiresDocuments: false },
  { value: "scooter", label: "Scooter / monopatín", requiresDocuments: false },
  { value: "car", label: "Auto", requiresDocuments: true },
  { value: "walking", label: "A pie", requiresDocuments: false },
  { value: "other", label: "Otro medio sin licencia", requiresDocuments: false },
];
const VALID_VEHICLE_TYPES = new Set(VEHICLE_OPTIONS.map(({ value }) => value));

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLatestAdultBirthDate = () => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setFullYear(date.getFullYear() - MINIMUM_RIDER_AGE);
  return formatDateInput(date);
};

const isAdultBirthDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [year, month, day] = value.split("-").map(Number);
  const birthDate = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return false;
  }
  const latestAdultBirthDate = new Date();
  latestAdultBirthDate.setHours(12, 0, 0, 0);
  latestAdultBirthDate.setFullYear(
    latestAdultBirthDate.getFullYear() - MINIMUM_RIDER_AGE
  );
  return birthDate <= latestAdultBirthDate;
};

function DocumentCapture({ documentType, file, onChange }) {
  const copy = DOCUMENT_COPY[documentType];
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <label className={`we-rider-document-capture${file ? " has-file" : ""}`}>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture={copy.capture}
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
      {previewUrl ? <img src={previewUrl} alt={`Vista previa de ${copy.label}`} /> : null}
      <span aria-hidden="true">{file ? "✓" : "+"}</span>
      <strong>{copy.label}</strong>
      <small>{file ? file.name : "Toca para tomar o elegir una foto"}</small>
    </label>
  );
}

function LiveSelfieCapture({ file, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastAnalysisAtRef = useRef(0);
  const challengeSequenceRef = useRef([]);
  const challengeIndexRef = useRef(0);
  const challengeStateRef = useRef(createChallengeState());
  const challengeStartedAtRef = useRef(0);
  const challengeScoresRef = useRef([]);
  const capturePendingRef = useRef(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [cameraStatus, setCameraStatus] = useState("idle");
  const [cameraError, setCameraError] = useState("");
  const [livenessStatus, setLivenessStatus] = useState("idle");
  const [livenessMessage, setLivenessMessage] = useState("");
  const [completedChallenges, setCompletedChallenges] = useState(0);

  const stopAnalysis = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    detectorRef.current?.close?.();
    detectorRef.current = null;
  };

  const stopCamera = (preserveResult = false) => {
    stopAnalysis();
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraStatus("idle");
    if (!preserveResult) {
      setLivenessStatus("idle");
      setLivenessMessage("");
      setCompletedChallenges(0);
    }
  };

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEffect(
    () => () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      detectorRef.current?.close?.();
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    },
    []
  );

  const captureFrame = async (livenessEvidence) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      capturePendingRef.current = false;
      setCameraError("No pudimos obtener la foto final. Vuelve a realizar la prueba.");
      stopCamera();
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      capturePendingRef.current = false;
      setCameraError("No pudimos preparar la selfie. Vuelve a intentar.");
      stopCamera();
      return;
    }

    const targetWidth = 960;
    const targetHeight = 1280;
    const targetRatio = targetWidth / targetHeight;
    const sourceRatio = video.videoWidth / video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = video.videoWidth;
    let sourceHeight = video.videoHeight;

    if (sourceRatio > targetRatio) {
      sourceWidth = video.videoHeight * targetRatio;
      sourceX = (video.videoWidth - sourceWidth) / 2;
    } else {
      sourceHeight = video.videoWidth / targetRatio;
      sourceY = (video.videoHeight - sourceHeight) / 2;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context.save();
    context.translate(targetWidth, 0);
    context.scale(-1, 1);
    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );
    context.restore();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) {
      capturePendingRef.current = false;
      setCameraError("No pudimos capturar la selfie. Vuelve a intentar.");
      stopCamera();
      return;
    }

    const capturedAt = new Date().toISOString();
    onCapture({
      file: new File([blob], "selfie-prueba-de-vida.jpg", { type: "image/jpeg" }),
      captureSource: "live_camera_liveness",
      capturedAt,
      livenessMethod: RIDER_LIVENESS_METHOD,
      livenessPassedAt: livenessEvidence.livenessPassedAt,
      livenessChallenges: livenessEvidence.livenessChallenges,
      livenessScore: livenessEvidence.livenessScore,
    });
    setCameraError("");
    setLivenessStatus("passed");
    setLivenessMessage("Prueba de vida aprobada");
    stopCamera(true);
    capturePendingRef.current = false;
  };

  const resetLivenessSession = () => {
    challengeSequenceRef.current = createLivenessChallengeSequence();
    challengeIndexRef.current = 0;
    challengeStateRef.current = createChallengeState();
    challengeStartedAtRef.current = Date.now();
    challengeScoresRef.current = [];
    capturePendingRef.current = false;
    setCompletedChallenges(0);
    setLivenessStatus("checking");
    setLivenessMessage("Mira de frente y mantén el rostro quieto");
  };

  const processLivenessResult = (analysis) => {
    if (analysis.faceCount === 0) {
      setLivenessMessage("Coloca tu rostro dentro del recuadro");
      return;
    }
    if (analysis.faceCount > 1) {
      setLivenessMessage("Debe aparecer una sola persona");
      return;
    }
    if (!analysis.faceReady) {
      setLivenessMessage("Centra el rostro y acércate un poco");
      return;
    }

    if (Date.now() - challengeStartedAtRef.current > LIVENESS_CHALLENGE_TIMEOUT_MS) {
      resetLivenessSession();
      setCameraError("La prueba venció y volvió a comenzar. Sigue las indicaciones.");
      return;
    }

    setCameraError("");
    const challenge = challengeSequenceRef.current[challengeIndexRef.current];
    if (!challenge) return;
    const next = advanceLivenessChallenge(
      challenge,
      challengeStateRef.current,
      analysis.scores
    );
    challengeStateRef.current = next.state;
    setLivenessMessage(getLivenessChallengeCopy(challenge, next.state.phase));

    if (!next.completed) return;

    challengeScoresRef.current.push(Number(next.score || 0));
    challengeIndexRef.current += 1;
    setCompletedChallenges(challengeIndexRef.current);

    if (challengeIndexRef.current < challengeSequenceRef.current.length) {
      challengeStateRef.current = createChallengeState();
      challengeStartedAtRef.current = Date.now();
      const nextChallenge = challengeSequenceRef.current[challengeIndexRef.current];
      setLivenessMessage(getLivenessChallengeCopy(nextChallenge, "neutral"));
      return;
    }

    if (capturePendingRef.current) return;
    capturePendingRef.current = true;
    const livenessPassedAt = new Date().toISOString();
    const livenessScore =
      challengeScoresRef.current.reduce((total, score) => total + score, 0) /
      challengeScoresRef.current.length;
    setLivenessStatus("passed");
    setLivenessMessage("Prueba aprobada. Tomando selfie...");
    window.setTimeout(
      () =>
        captureFrame({
          livenessPassedAt,
          livenessChallenges: challengeSequenceRef.current,
          livenessScore: Number(livenessScore.toFixed(5)),
        }),
      350
    );
  };

  const runLivenessAnalysis = (frameTimestamp) => {
    if (!detectorRef.current || !streamRef.current || capturePendingRef.current) return;
    const now = Number(frameTimestamp) || performance.now();
    if (now - lastAnalysisAtRef.current >= 90 && videoRef.current?.videoWidth > 0) {
      lastAnalysisAtRef.current = now;
      try {
        processLivenessResult(detectorRef.current.analyze(videoRef.current, now));
      } catch (error) {
        setCameraError("No pudimos analizar el rostro. Vuelve a abrir la cámara.");
        stopCamera();
        return;
      }
    }
    animationFrameRef.current = requestAnimationFrame(runLivenessAnalysis);
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(
        "Este dispositivo no permite usar la cámara desde aquí. Abre We! Rider desde el launcher o un navegador actualizado."
      );
      return;
    }

    setCameraStatus("opening");
    setLivenessStatus("preparing");
    setLivenessMessage("Preparando la validación facial...");
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
      });
      streamRef.current = stream;
      if (!videoRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraStatus("active");
      detectorRef.current = await createFaceLivenessDetector();
      if (streamRef.current !== stream) {
        detectorRef.current?.close?.();
        detectorRef.current = null;
        return;
      }
      resetLivenessSession();
      animationFrameRef.current = requestAnimationFrame(runLivenessAnalysis);
    } catch (error) {
      stopAnalysis();
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraStatus("idle");
      if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
        setCameraError(
          "Permite el acceso a la cámara para tomar la selfie en este momento."
        );
      } else if (error?.name === "NotFoundError") {
        setCameraError("No encontramos una cámara frontal disponible.");
      } else {
        setCameraError(
          "No pudimos iniciar la validación facial. Revisa tu conexión y vuelve a intentar."
        );
      }
    }
  };

  const cameraActive = cameraStatus === "active";

  return (
    <div className={`we-rider-live-selfie${file ? " has-file" : ""}`}>
      <div className="we-rider-live-selfie-stage">
        <video
          ref={videoRef}
          className={cameraActive ? "is-visible" : ""}
          autoPlay
          muted
          playsInline
          aria-label="Vista en vivo de la cámara frontal"
        />
        {!cameraActive && previewUrl ? (
          <img src={previewUrl} alt="Selfie tomada en vivo" />
        ) : null}
        {!cameraActive && !previewUrl ? (
          <div className="we-rider-live-selfie-placeholder" aria-hidden="true">
            <span>●</span>
            <strong>Cámara frontal</strong>
          </div>
        ) : null}
        {cameraStatus === "opening" ? (
          <div className="we-rider-live-selfie-loading">Preparando cámara...</div>
        ) : null}
        {cameraActive ? (
          <div className={`we-rider-liveness-guide is-${livenessStatus}`} aria-live="polite">
            <strong>{livenessMessage}</strong>
            <span>
              {completedChallenges} de 2 movimientos completados
            </span>
          </div>
        ) : null}
      </div>

      <canvas ref={canvasRef} hidden />

      <div className="we-rider-live-selfie-actions">
        {cameraActive ? (
          <button type="button" className="we-rider-secondary" onClick={() => stopCamera()}>
            Cancelar
          </button>
        ) : (
          <button
            type="button"
            className="we-rider-primary"
            disabled={cameraStatus === "opening"}
            onClick={startCamera}
          >
            {file ? "Volver a tomarla" : "Abrir cámara"}
          </button>
        )}
      </div>

      <p className="we-rider-live-selfie-note">
        Sigue los movimientos en pantalla. La selfie se toma automáticamente cuando
        completas la prueba de vida y no puede elegirse desde la galería.
      </p>
      {!cameraActive && file && livenessStatus === "passed" ? (
        <p className="we-rider-live-selfie-success">✓ Prueba de vida aprobada</p>
      ) : null}
      {cameraError ? (
        <p className="we-rider-live-selfie-error" role="alert">{cameraError}</p>
      ) : null}
    </div>
  );
}

const validateFile = (file) => {
  if (!file) return "Selecciona una imagen.";
  if (!ALLOWED_FILE_TYPES.has(String(file.type || ""))) {
    return "Usa una imagen JPG, PNG o WEBP.";
  }
  if (Number(file.size || 0) > MAX_FILE_SIZE) {
    return "La imagen puede pesar hasta 8 MB.";
  }
  if (Number(file.size || 0) <= 0) return "La imagen está vacía.";
  return "";
};

export default function WeRiderIdentityOnboarding({
  profile,
  compliance,
  onSubmit,
  onUpdateProfile,
  onSignOut,
}) {
  const [step, setStep] = useState(0);
  const [personal, setPersonal] = useState({
    legalFullName: profile?.fullName || "",
    documentNumber: "",
    birthDate: "",
  });
  const [documents, setDocuments] = useState({});
  const [selfieEvidence, setSelfieEvidence] = useState(null);
  const [vehicle, setVehicle] = useState({
    type: profile?.vehicleType || "",
    reference: profile?.vehicleReference || "",
  });
  const [consents, setConsents] = useState({
    ageAndAccuracy: false,
    manualIdentityReview: false,
    termsAndPrivacy: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const motorized = ["motorcycle", "car"].includes(vehicle.type);

  const steps = useMemo(
    () => [
      { id: "personal", label: "Datos" },
      { id: "dni", label: "DNI" },
      { id: "selfie", label: "Selfie" },
      ...(motorized ? [{ id: "vehicle", label: "Vehículo" }] : []),
      { id: "review", label: "Confirmar" },
    ],
    [motorized]
  );
  const currentStep = steps[step]?.id || "personal";
  const requiredDocumentTypes = getRequiredWeRiderDocumentTypes(
    vehicle.type
  );

  const selectFile = (documentType, file) => {
    const validationError = file ? validateFile(file) : "";
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setDocuments((current) => ({ ...current, [documentType]: file }));
  };

  const validateCurrentStep = () => {
    if (currentStep === "personal") {
      if (personal.legalFullName.trim().split(/\s+/).filter(Boolean).length < 2) {
        return "Ingresa tu nombre y apellido tal como aparecen en el DNI.";
      }
      if (!/^\d{7,9}$/.test(personal.documentNumber.replace(/\D/g, ""))) {
        return "Ingresa un DNI válido.";
      }
      const birthTime = new Date(`${personal.birthDate}T12:00:00`).getTime();
      if (!personal.birthDate || !Number.isFinite(birthTime) || birthTime >= Date.now()) {
        return "Ingresa una fecha de nacimiento válida.";
      }
      if (!isAdultBirthDate(personal.birthDate)) {
        return "Para registrarte como Rider debes tener 18 años o más.";
      }
      if (!VALID_VEHICLE_TYPES.has(vehicle.type)) {
        return "Selecciona el medio con el que harás las entregas.";
      }
    }
    if (currentStep === "dni" && (!documents.dni_front || !documents.dni_back)) {
      return "Adjunta el frente y el dorso del DNI.";
    }
    if (currentStep === "selfie" && !documents.selfie) {
      return "Toma una selfie actual para continuar.";
    }
    if (currentStep === "selfie") {
      const capturedAt = new Date(selfieEvidence?.capturedAt || "").getTime();
      const livenessPassedAt = new Date(
        selfieEvidence?.livenessPassedAt || ""
      ).getTime();
      const livenessChallenges = selfieEvidence?.livenessChallenges || [];
      if (
        selfieEvidence?.captureSource !== "live_camera_liveness" ||
        selfieEvidence?.livenessMethod !== RIDER_LIVENESS_METHOD ||
        !Array.isArray(livenessChallenges) ||
        !["blink", "mouth_open"].every((challenge) =>
          livenessChallenges.includes(challenge)
        ) ||
        Number(selfieEvidence?.livenessScore || 0) < 0.38 ||
        !Number.isFinite(capturedAt) ||
        !Number.isFinite(livenessPassedAt) ||
        Date.now() - capturedAt > LIVE_SELFIE_MAX_AGE_MS ||
        Date.now() - livenessPassedAt > LIVE_SELFIE_MAX_AGE_MS ||
        capturedAt > Date.now() + 60_000 ||
        capturedAt < livenessPassedAt - 5_000 ||
        capturedAt - livenessPassedAt > 60_000
      ) {
        return "Completa nuevamente la prueba de vida para continuar.";
      }
    }
    if (
      currentStep === "vehicle" &&
      [
        "driver_license_front",
        "driver_license_back",
        "vehicle_registration_front",
        "vehicle_registration_back",
      ].some((documentType) => !documents[documentType])
    ) {
      return "Adjunta ambos lados de la licencia y de la cédula del vehículo.";
    }
    return "";
  };

  const continueToNextStep = async () => {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    if (
      currentStep === "personal" &&
      onUpdateProfile &&
      (vehicle.type !== profile?.vehicleType ||
        vehicle.reference.trim() !== String(profile?.vehicleReference || "").trim())
    ) {
      setSubmitting(true);
      try {
        await onUpdateProfile({
          fullName: profile?.fullName || personal.legalFullName,
          phone: profile?.phone || "",
          vehicleType: vehicle.type,
          vehicleReference: vehicle.reference.trim(),
        });
      } catch (requestError) {
        setError(requestError?.message || "No pudimos actualizar tu medio de reparto.");
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    setStep((current) => Math.min(steps.length - 1, current + 1));
  };

  const submitIdentity = async () => {
    if (!consents.ageAndAccuracy) {
      setError("Confirma que tienes 18 años o más y que los datos son reales.");
      return;
    }
    if (!consents.manualIdentityReview) {
      setError("Autoriza la revisión manual de identidad y documentación.");
      return;
    }
    if (!consents.termsAndPrivacy) {
      setError("Acepta los términos de Rider y la política de privacidad.");
      return;
    }
    const missingDocument = requiredDocumentTypes.find(
      (documentType) => !documents[documentType]
    );
    if (missingDocument) {
      setError("Falta adjuntar uno de los documentos requeridos.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        ...personal,
        documentNumber: personal.documentNumber.replace(/\D/g, ""),
        consents: {
          ageAndAccuracyAccepted: true,
          manualIdentityReviewAccepted: true,
          sensitiveDataProcessingAccepted: true,
          termsAccepted: true,
          privacyAccepted: true,
          identityConsentVersion: RIDER_IDENTITY_CONSENT_VERSION,
          termsVersion: RIDER_TERMS_VERSION,
          privacyVersion: RIDER_PRIVACY_VERSION,
        },
        documents: requiredDocumentTypes.map((documentType) => ({
          type: documentType,
          file: documents[documentType],
          ...(documentType === "selfie" ? selfieEvidence : {}),
        })),
      });
    } catch (requestError) {
      setError(requestError?.message || "No pudimos presentar tu documentación.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="we-rider-onboarding-shell">
      <header className="we-rider-onboarding-header">
        <div>
          <img src={weRiderLogo} alt="" />
          <span><strong>We! Rider</strong><small>Habilitación</small></span>
        </div>
        <button type="button" onClick={onSignOut}>Cerrar sesión</button>
      </header>

      <section className="we-rider-onboarding-panel">
        <div className="we-rider-onboarding-heading">
          <span className="we-rider-eyebrow">Validación de identidad</span>
          <h1>Completa tu habilitación</h1>
          <p>Necesitamos confirmar tu identidad y la documentación con la que realizarás las entregas.</p>
          {compliance?.statusReason ? (
            <div className="we-rider-onboarding-observation">
              <strong>Observación</strong>
              <span>{compliance.statusReason}</span>
            </div>
          ) : null}
        </div>

        <ol className="we-rider-onboarding-steps" aria-label="Pasos de habilitación">
          {steps.map((item, index) => (
            <li
              key={item.id}
              className={index === step ? "is-active" : index < step ? "is-complete" : ""}
            >
              <span>{index < step ? "✓" : index + 1}</span>
              <small>{item.label}</small>
            </li>
          ))}
        </ol>

        <div className="we-rider-onboarding-content">
          {currentStep === "personal" ? (
            <div className="we-rider-onboarding-form">
              <h2>Datos del DNI</h2>
              <p>Escríbelos exactamente como figuran en el documento.</p>
              <label>
                Nombre y apellido legal
                <input
                  autoComplete="name"
                  value={personal.legalFullName}
                  onChange={(event) =>
                    setPersonal({ ...personal, legalFullName: event.target.value })
                  }
                />
              </label>
              <div className="we-rider-form-row">
                <label>
                  Número de DNI
                  <input
                    inputMode="numeric"
                    autoComplete="off"
                    value={personal.documentNumber}
                    onChange={(event) =>
                      setPersonal({
                        ...personal,
                        documentNumber: event.target.value.replace(/\D/g, "").slice(0, 9),
                      })
                    }
                    placeholder="Sin puntos"
                  />
                </label>
                <label>
                  Fecha de nacimiento
                  <input
                    type="date"
                    max={getLatestAdultBirthDate()}
                    value={personal.birthDate}
                    onChange={(event) =>
                      setPersonal({ ...personal, birthDate: event.target.value })
                    }
                  />
                </label>
              </div>
              <div className="we-rider-form-row">
                <label>
                  Medio de reparto
                  <select
                    value={vehicle.type}
                    onChange={(event) =>
                      setVehicle({ ...vehicle, type: event.target.value })
                    }
                    required
                  >
                    <option value="" disabled>Selecciona tu medio</option>
                    {VEHICLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Identificación del medio
                  <input
                    value={vehicle.reference}
                    onChange={(event) =>
                      setVehicle({ ...vehicle, reference: event.target.value })
                    }
                    placeholder={motorized ? "Patente o descripción" : "Color o descripción"}
                  />
                </label>
              </div>
              <p className="we-rider-vehicle-document-note">
                Sólo Moto y Auto requieren licencia de conducir y cédula.
              </p>
            </div>
          ) : null}

          {currentStep === "dni" ? (
            <div>
              <h2>Fotografía tu DNI</h2>
              <p>Usa buena luz, muestra las cuatro esquinas y evita reflejos.</p>
              <div className="we-rider-document-grid">
                {['dni_front', 'dni_back'].map((documentType) => (
                  <DocumentCapture
                    key={documentType}
                    documentType={documentType}
                    file={documents[documentType]}
                    onChange={(file) => selectFile(documentType, file)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {currentStep === "selfie" ? (
            <div>
              <h2>Toma una selfie</h2>
              <p>Mira de frente, sin casco, anteojos oscuros ni otras personas en la imagen.</p>
              <p className="we-rider-biometric-privacy-note">
                Los movimientos se analizan en este dispositivo. No guardamos el video:
                sólo se envían la selfie final y el resultado de la prueba de vida. La
                coincidencia con el DNI será revisada manualmente por personal autorizado.
              </p>
              <div className="we-rider-document-grid is-single">
                <LiveSelfieCapture
                  file={documents.selfie}
                  onCapture={(evidence) => {
                    selectFile("selfie", evidence.file);
                    setSelfieEvidence({
                      captureSource: evidence.captureSource,
                      capturedAt: evidence.capturedAt,
                      livenessMethod: evidence.livenessMethod,
                      livenessPassedAt: evidence.livenessPassedAt,
                      livenessChallenges: evidence.livenessChallenges,
                      livenessScore: evidence.livenessScore,
                    });
                  }}
                />
              </div>
            </div>
          ) : null}

          {currentStep === "vehicle" ? (
            <div>
              <h2>Documentación del vehículo</h2>
              <p>La licencia y la cédula deben estar vigentes y verse completas.</p>
              <div className="we-rider-document-grid">
                {[
                  "driver_license_front",
                  "driver_license_back",
                  "vehicle_registration_front",
                  "vehicle_registration_back",
                ].map((documentType) => (
                  <DocumentCapture
                    key={documentType}
                    documentType={documentType}
                    file={documents[documentType]}
                    onChange={(file) => selectFile(documentType, file)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {currentStep === "review" ? (
            <div className="we-rider-onboarding-review">
              <h2>Revisa antes de enviar</h2>
              <dl>
                <div><dt>Nombre legal</dt><dd>{personal.legalFullName}</dd></div>
                <div><dt>DNI</dt><dd>{personal.documentNumber}</dd></div>
                <div><dt>Nacimiento</dt><dd>{personal.birthDate}</dd></div>
                <div>
                  <dt>Medio de reparto</dt>
                  <dd>
                    {VEHICLE_OPTIONS.find(({ value }) => value === vehicle.type)?.label || vehicle.type}
                    {vehicle.reference ? ` · ${vehicle.reference}` : ""}
                  </dd>
                </div>
              </dl>
              <div className="we-rider-onboarding-files">
                {requiredDocumentTypes.map((documentType) => (
                  <span key={documentType}>✓ {DOCUMENT_COPY[documentType].label}</span>
                ))}
              </div>
              <label className="we-rider-onboarding-declaration">
                <input
                  type="checkbox"
                  checked={consents.ageAndAccuracy}
                  onChange={(event) =>
                    setConsents({ ...consents, ageAndAccuracy: event.target.checked })
                  }
                />
                <span>
                  Declaro que tengo 18 años o más y que los datos y documentos son
                  propios, reales y están actualizados.
                </span>
              </label>
              <label className="we-rider-onboarding-declaration">
                <input
                  type="checkbox"
                  checked={consents.manualIdentityReview}
                  onChange={(event) =>
                    setConsents({
                      ...consents,
                      manualIdentityReview: event.target.checked,
                    })
                  }
                />
                <span>
                  Autorizo el tratamiento de mi DNI, selfie, prueba de vida y
                  documentación para una revisión manual de identidad y prevención de
                  fraude.
                </span>
              </label>
              <label className="we-rider-onboarding-declaration">
                <input
                  type="checkbox"
                  checked={consents.termsAndPrivacy}
                  onChange={(event) =>
                    setConsents({ ...consents, termsAndPrivacy: event.target.checked })
                  }
                />
                <span>
                  Leí y acepto los{" "}
                  <a href="/terminos-de-uso" target="_blank" rel="noreferrer">
                    términos de uso para Riders
                  </a>{" "}
                  y la{" "}
                  <a href="/politica-de-privacidad" target="_blank" rel="noreferrer">
                    política de privacidad
                  </a>
                  .
                </span>
              </label>
            </div>
          ) : null}
        </div>

        {error ? <p className="we-rider-message is-error" role="alert">{error}</p> : null}

        <footer className="we-rider-onboarding-actions">
          <button
            type="button"
            className="we-rider-secondary"
            disabled={step === 0 || submitting}
            onClick={() => {
              setError("");
              setStep((current) => Math.max(0, current - 1));
            }}
          >
            Volver
          </button>
          {currentStep === "review" ? (
            <button
              type="button"
              className="we-rider-primary"
              disabled={submitting}
              onClick={submitIdentity}
            >
              {submitting ? "Enviando..." : "Enviar documentación"}
            </button>
          ) : (
            <button
              type="button"
              className="we-rider-primary"
              disabled={submitting}
              onClick={continueToNextStep}
            >
              {submitting ? "Guardando..." : "Continuar"}
            </button>
          )}
        </footer>
      </section>
    </main>
  );
}
